import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";

import { TYPES } from "./index";

// FeedTypeChips' own values, minus "All" (null) — a deep-linked ?type= outside this set is ignored.
const DEEP_LINKABLE_TYPES = TYPES.map(({ value }) => value).filter(Boolean);

/**
 * The My Feed type filter's state, seeded from a `?type=` in the URL (e.g. the upcoming-events
 * panel's "All events" link, GROWTH-7131). Whoever owns the filter calls this: the Feed page since
 * GROWTH-7573 (the filter lives in its sidebar), FeedStream when used on its own.
 *
 * Read on the very first render (so a fresh page load never wastes a request on "All" first) and
 * re-synced on every navigation afterward (`location.key`), for a Link click that lands on an
 * already-mounted page (same route, no remount) rather than a fresh navigation. One-directional
 * only: a filter click never writes back to the URL, so after picking a different type the URL can
 * still read `?type=event` — keying on the navigation rather than on the query string is what makes
 * a second "All events" click to that same URL re-apply the filter (PR 5577 review CODE-6).
 *
 * @param {boolean} enabled false = ignore the URL entirely (the Saved page), type stays null until set
 * @returns {[string|null, Function]} [type, setType]
 */
const useDeepLinkedType = enabled => {
  const location = useLocation();
  const deepLinkedType = useCallback(
    search => {
      if (!enabled) return null;
      const requested = queryString.parse(search).type;
      return DEEP_LINKABLE_TYPES.includes(requested) ? requested : null;
    },
    [enabled]
  );
  const [type, setType] = useState(() => deepLinkedType(location.search));

  useEffect(() => {
    setType(deepLinkedType(location.search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedType, location.search, location.key]);

  return [type, setType];
};

export default useDeepLinkedType;
